<template>
  <div class="captcha-container">
    <div class="captcha-question">
      <span>{{ question }}</span>
      <button @click="generateNewQuestion" class="refresh-btn" :title="$t('captcha.refresh')">
        🔄
      </button>
    </div>
    <div class="captcha-input">
      <input 
        v-model="userAnswer" 
        type="number" 
        :placeholder="$t('captcha.placeholder')"
        class="captcha-input-field"
        @input="onAnswerChange"
      />
      <span v-if="isCorrect !== null" class="captcha-status" :class="{ correct: isCorrect, incorrect: !isCorrect }">
        {{ isCorrect ? '✓' : '✗' }}
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

// 验证码状态
const num1 = ref(0)
const num2 = ref(0)
const operation = ref<'add' | 'subtract' | 'multiply'>('add')
const userAnswer = ref('')
const isCorrect = ref<boolean | null>(null)

// 生成随机数
const randomInt = (min: number, max: number) => {
  return Math.floor(Math.random() * (max - min + 1)) + min
}

// 生成新题目
const generateNewQuestion = () => {
  const operations = ['add', 'subtract', 'multiply'] as const
  operation.value = operations[randomInt(0, 2)]
  
  switch (operation.value) {
    case 'add':
      num1.value = randomInt(1, 20)
      num2.value = randomInt(1, 20)
      break
    case 'subtract':
      num1.value = randomInt(10, 30)
      num2.value = randomInt(1, num1.value)
      break
    case 'multiply':
      num1.value = randomInt(2, 9)
      num2.value = randomInt(2, 9)
      break
  }
  
  userAnswer.value = ''
  isCorrect.value = null
}

// 计算正确答案
const correctAnswer = computed(() => {
  switch (operation.value) {
    case 'add':
      return num1.value + num2.value
    case 'subtract':
      return num1.value - num2.value
    case 'multiply':
      return num1.value * num2.value
    default:
      return 0
  }
})

// 题目显示
const question = computed(() => {
  const op = operation.value === 'add' ? '+' : operation.value === 'subtract' ? '-' : '×'
  return `${num1.value} ${op} ${num2.value} = ?`
})

// 检查答案
const onAnswerChange = () => {
  if (userAnswer.value === '') {
    isCorrect.value = null
    return
  }
  
  const answer = parseInt(userAnswer.value)
  isCorrect.value = answer === correctAnswer.value
}

// 验证答案是否正确
const isValid = computed(() => {
  return isCorrect.value === true
})

// 重置验证码
const reset = () => {
  generateNewQuestion()
}

// 暴露给父组件的方法
defineExpose({
  isValid,
  reset
})

// 组件挂载时生成第一道题
onMounted(() => {
  generateNewQuestion()
})
</script>

<style scoped>
.captcha-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
}

.captcha-question {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
  color: #333;
}

.refresh-btn {
  background: none;
  border: none;
  font-size: 16px;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: background-color 0.2s ease;
}

.refresh-btn:hover {
  background: #e9ecef;
}

.captcha-input {
  display: flex;
  align-items: center;
  gap: 8px;
}

.captcha-input-field {
  flex: 1;
  padding: 8px 12px;
  border: 2px solid #e9ecef;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s ease;
}

.captcha-input-field:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.captcha-status {
  font-size: 18px;
  font-weight: bold;
  min-width: 20px;
  text-align: center;
}

.captcha-status.correct {
  color: #28a745;
}

.captcha-status.incorrect {
  color: #dc3545;
}
</style>
